/**
 * Shared constants and tiny components reused across Overview / Domains / Usage tabs.
 * Kept separate to stay under the 200-line-per-file guideline.
 */
import React from 'react';
import { Tooltip } from '@vnetwork/ui/primitives';
import type { OpsCategory, OpsServiceStatus } from '@/api/operations';

/* ── Status pill tone + tooltip ──────────────────────────────────────────── */

export type StatusPillTone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'neutral';

export const STATUS_TONE: Record<string, StatusPillTone> = {
  active: 'success',
  pending: 'warning',
  suspended: 'danger',
  terminated: 'muted',
  error: 'danger',
};

export const STATUS_TOOLTIP: Record<OpsServiceStatus | string, string> = {
  active: 'Service is running normally',
  pending: 'Service is being provisioned',
  suspended: 'Service is suspended',
  terminated: 'Service has been terminated',
  error: 'Service is in an error state',
};

/* ── Category badge tone + label ─────────────────────────────────────────── */

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export const CATEGORY_TONE: Record<OpsCategory, BadgeTone> = {
  cdn: 'info',
  waap: 'warning',
  storage: 'neutral',
  media: 'success',
  compute: 'info',
  registrar: 'neutral',
  other: 'neutral',
};

export const CATEGORY_LABEL: Record<OpsCategory, string> = {
  cdn: 'CDN',
  waap: 'WAAP',
  storage: 'Storage',
  media: 'Media',
  compute: 'Compute',
  registrar: 'Registrar',
  other: 'Other',
};

/* ── InfoGrid — read-only label / value fact grid ────────────────────────── */

export interface InfoRow {
  label: string;
  value: React.ReactNode;
}

/**
 * Two-column label/value grid.  Each pair is a full-width row (label = fixed
 * width right column, value = flexible left).  Sanctioned hand-roll (ui-conventions:
 * @vnetwork/ui has no generic InfoGrid; SettingRow is for control rows).
 */
export function InfoGrid({ rows }: { rows: InfoRow[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        gap: 'var(--vn-space-2) var(--vn-space-6)',
        fontSize: 'var(--vn-font-size-sm)',
      }}
    >
      {rows.map(({ label, value }) => (
        <React.Fragment key={label}>
          <span style={{ color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ color: 'var(--vn-fg-default)', wordBreak: 'break-all' }}>{value}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Section heading ─────────────────────────────────────────────────────── */

/**
 * Small uppercase section heading used inside card bodies
 * (mirrors convention from ops-service-columns.tsx).
 */
export function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        fontSize: 'var(--vn-font-size-xs)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--vn-fg-muted)',
        marginBottom: 'var(--vn-space-3)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ── Monospace snippet ───────────────────────────────────────────────────── */

export function MonoText({
  children,
  tooltip,
}: {
  children: React.ReactNode;
  tooltip?: string;
}) {
  const inner = (
    <span
      style={{
        fontFamily: 'monospace',
        fontSize: 'var(--vn-font-size-xs)',
        color: 'var(--vn-fg-default)',
      }}
    >
      {children}
    </span>
  );
  if (tooltip) {
    return <Tooltip content={tooltip}>{inner}</Tooltip>;
  }
  return inner;
}
